<template>
  <v-container class="rm-drawer-panel">
    <RightDrawerHeader :title="componentTitle" showBack @back="cancelDeliveryClient" />
    <v-form
      ref="deliveryClientForm"
      v-model="deliveryClientFormValid"
      class="rm-drawer-body"
      @submit.prevent="submit"
    >
      <v-row class="rm-drawer-scroll">
        <v-col cols="12">
          <h3 class="font-weight-bold">
            {{ $t('REPAIR_FILE_DELIVERY_CLIENT_DRAWER.SUB_TITLE.DELIVERY_INFORMATION') }}
          </h3>
        </v-col>

        <!--END_STATUS-->
        <v-col cols="12">
          <p required="true" class="custom-label">
            {{ $t('REPAIR_FILE_DELIVERY_CLIENT_DRAWER.SELECT.END_STATUS') }}
          </p>
          <v-autocomplete
            id="select-end-status"
            v-model="selectedEndStatus"
            :items="endStatusList"
            :no-data-text="$t('COMMON.NO_DATA')"
            :placeholder="$t('REPAIR_FILE_DELIVERY_CLIENT_DRAWER.PLACEHOLDER.END_STATUS')"
            hide-details
            item-title="name"
            item-value="technical_id"
            required
            :rules="requiredRules"
            :disabled="!currentRepairFile.product_boomerang_id"
          >
            <template v-slot:selection="{ item }">
              <v-chip>
                <span>{{ item.technical_id }}</span>
                <span>{{ item.title }}</span>
              </v-chip>
            </template>
            <template v-slot:item="{ item, props }">
              <v-list-item :title="item.title" :value="item.value" v-bind="props">
                <template #title>
                  <span class="truncate" :title="item.title">
                    {{ item.title }}
                  </span>
                </template>
              </v-list-item>
            </template>
          </v-autocomplete>
        </v-col>
        <!--PRODUCT BOOMERANG ID-->
        <v-col cols="12" v-if="deliveryClient && !showReplacementProduct">
          <p class="custom-label">
            {{ $t('REPAIR_FILE_DELIVERY_CLIENT_DRAWER.SELECT.PRODUCT_BOOMERANG_NAME') }}
          </p>
          <v-text-field
            v-model="productBoomerangName"
            disabled
            :error-messages="error?.productBoomerangName"
            :hide-details="!error?.productBoomerangName"
          />
        </v-col>
        <!--SELECT PRODUCT BOOMERANG ID-->
        <v-col cols="12" v-if="deliveryClient && showReplacementProduct">
          <p required="true" class="custom-label">
            {{ $t('REPAIR_FILE_DELIVERY_CLIENT_DRAWER.SELECT.SELECT_PRODUCT') }}
          </p>
          <custom-autocomplete
            id="select-product"
            v-model="selectedProduct"
            :items="productList"
            :no-data-text="$t('COMMON.NO_DATA')"
            :placeholder="$t('REPAIR_FILE_DELIVERY_CLIENT_DRAWER.PLACEHOLDER.PRODUCT')"
            hide-details
            item-title="boomerang_name"
            item-value="technical_id"
            :disabled="!currentRepairFile.product_boomerang_id"
          />
        </v-col>
        <!--SERIAL NUMBER-->
        <v-col cols="12" v-if="deliveryClient">
          <div class="d-flex justify-lg-space-between align-start">
            <p required="true" class="custom-label">
              {{ $t('REPAIR_FILE_DELIVERY_CLIENT_DRAWER.SELECT.SERIAL_NUMBER') }}
            </p>
            <button
              v-if="product?.serial_number_picture"
              type="button"
              :aria-label="$t('REPAIR_FILE_DELIVERY_CLIENT_DRAWER.BUTTON.SERIAL_NUMBER')"
              class="serial-picture-btn"
              @click="openSerialNumberImage"
            >
              <v-icon icon="mdi-image-search-outline" size="18" />
              {{ $t('REPAIR_FILE_DELIVERY_CLIENT_DRAWER.BUTTON.SERIAL_NUMBER') }}
            </button>
          </div>
          <v-text-field
            v-model="serialNumber"
            :placeholder="$t('REPAIR_FILE_DELIVERY_CLIENT_DRAWER.PLACEHOLDER.SEARCH')"
            :disabled="
              (!selectedProduct && showReplacementProduct) ||
              !currentRepairFile.product_boomerang_id
            "
            :rules="requiredRules"
            :error-messages="error?.serialNumber"
            :hide-details="!error?.serialNumber"
          />
          <p class="custom-hint text-warning" v-if="isGenericSerialNumber">
            {{ $t('REPAIR_FILE_DELIVERY_CLIENT_DRAWER.MESSAGE.GENERIC_SERIAL_NUMBER') }}
          </p>
        </v-col>
        <!--PRODUCT END COST-->
        <v-col cols="12" v-if="deliveryClient">
          <p required="true" class="custom-label">
            {{ $t('REPAIR_FILE_DELIVERY_CLIENT_DRAWER.SELECT.PRODUCT_END_COST') }}
          </p>
          <v-text-field
            :disabled="!serialNumber || !currentRepairFile.product_boomerang_id"
            v-model="productEndCost"
            required
            type="number"
            :rules="[...isNumberRules, ...positiveNumberRules]"
            :error-messages="error?.productEndCost"
            :hide-details="!error?.productEndCost"
            :placeholder="$t('REPAIR_FILE_DELIVERY_CLIENT_DRAWER.PLACEHOLDER.PRODUCT_END_COST')"
          >
            <template #append-inner>
              <span class="euro-suffix">{{ $t('COMMON.EURO') }}</span>
            </template>
          </v-text-field>
        </v-col>

        <!--BILLING NUMBER-->
        <v-col cols="12">
          <p class="custom-label">
            {{ $t('REPAIR_FILE_DELIVERY_CLIENT_DRAWER.SUB_TITLE.BILLING_NUMBER') }}
          </p>
          <v-text-field
            v-model="billingNumber"
            maxlength="20"
            :error-messages="error?.billingNumber"
            :disabled="!currentRepairFile.product_boomerang_id"
            :hide-details="!error?.billingNumber"
            :placeholder="$t('REPAIR_FILE_DELIVERY_CLIENT_DRAWER.PLACEHOLDER.BILLING_NUMBER')"
          />
        </v-col>

        <v-col cols="12">
          <v-row>
            <v-col cols="12">
              <div class="d-flex align-center justify-space-between">
                <h3 class="font-weight-bold">
                  {{ $t('REPAIR_FILE_DELIVERY_CLIENT_DRAWER.SUB_TITLE.BILLING_CLIENT_COST') }}
                </h3>
              </div>
            </v-col>

            <v-col cols="12">
              <v-card-secondary class="delivery-price-card">
                <div class="delivery-price-row">
                  <span class="delivery-price-label">
                    {{ $t('REPAIR_FILE_DELIVERY_CLIENT_DRAWER.SUB_TITLE.REPAIR_CHANGE_COST') }}
                  </span>
                  <v-text-field
                    class="number-text-field"
                    v-model="packageClientPrice"
                    density="compact"
                    type="number"
                    :error-messages="error?.packageClientPrice"
                    :disabled="!currentRepairFile.product_boomerang_id"
                    :hide-details="!error?.packageClientPrice"
                    :placeholder="
                      $t('REPAIR_FILE_DELIVERY_CLIENT_DRAWER.PLACEHOLDER.REPAIR_CHANGE_COST')
                    "
                  >
                    <template #append-inner>
                      <span class="euro-suffix">{{ $t('COMMON.EURO') }}</span>
                    </template>
                  </v-text-field>
                </div>

                <div class="delivery-price-row">
                  <div>
                    <p class="delivery-price-label mb-1">
                      {{ $t('REPAIR_FILE_DELIVERY_CLIENT_DRAWER.SUB_TITLE.TRANSPORT') }}
                    </p>
                    <div class="d-flex">
                      <p class="detail-info-label mb-0">
                        {{ $t('REPAIR_FILE_DELIVERY_CLIENT_DRAWER.SUB_TITLE.REF_ERP') }}
                      </p>
                      <p class="detail-info-value ml-2 mb-0">
                        {{ currentRepairFile.initial_package_transport_ref_code }}
                      </p>
                    </div>
                  </div>
                  <v-text-field
                    class="number-text-field"
                    v-model="transportCost"
                    density="compact"
                    type="number"
                    :error-messages="error?.transportCost"
                    :disabled="!currentRepairFile.product_boomerang_id"
                    :hide-details="!error?.transportCost"
                    :placeholder="$t('REPAIR_FILE_DELIVERY_CLIENT_DRAWER.PLACEHOLDER.TRANSPORT')"
                  >
                    <template #append-inner>
                      <span class="euro-suffix">{{ $t('COMMON.EURO') }}</span>
                    </template>
                  </v-text-field>
                </div>

                <div class="delivery-price-row">
                  <div>
                    <p class="delivery-price-label mb-1">
                      {{ $t('REPAIR_FILE_DELIVERY_CLIENT_DRAWER.SUB_TITLE.MANAGEMENT') }}
                    </p>
                    <div class="d-flex">
                      <p class="detail-info-label mb-0">
                        {{ $t('REPAIR_FILE_DELIVERY_CLIENT_DRAWER.SUB_TITLE.REF_ERP') }}
                      </p>
                      <p class="detail-info-value ml-2 mb-0">
                        {{ currentRepairFile.initial_customer_code }}
                      </p>
                    </div>
                  </div>
                  <v-text-field
                    class="number-text-field"
                    v-model="additionalRepairCost"
                    density="compact"
                    type="number"
                    :error-messages="error?.additionalRepairCost"
                    :disabled="!currentRepairFile.product_boomerang_id"
                    :hide-details="!error?.additionalRepairCost"
                    :placeholder="$t('REPAIR_FILE_DELIVERY_CLIENT_DRAWER.PLACEHOLDER.MANAGEMENT')"
                  >
                    <template #append-inner>
                      <span class="euro-suffix">{{ $t('COMMON.EURO') }}</span>
                    </template>
                  </v-text-field>
                </div>

                <v-divider class="delivery-price-divider my-3" />
                <div class="delivery-price-total-row">
                  <span>{{ $t('REPAIR_FILE_DELIVERY_CLIENT_DRAWER.SELECT.TOTAL_HT') }}</span>
                  <strong>
                    {{ getTotalCost() }}
                    {{ $t('COMMON.EURO') }}
                  </strong>
                </div>
              </v-card-secondary>
            </v-col>
          </v-row>
        </v-col>

        <v-col cols="12">
          <p class="custom-label">
            {{ $t('REPAIR_FILE_DELIVERY_CLIENT_DRAWER.SUB_TITLE.DELIVERY_DATE') }}
          </p>
          <CustomDateInput
            :model-value="realDeliveryDate"
            @update:modelValue="realDeliveryDate = $event"
            :max-date="today"
            :min-date="currentRepairFile.finalization_date"
          />
          <p class="custom-hint">
            {{ $t('REPAIR_FILE_DELIVERY_CLIENT_DRAWER.DETAIL.DELIVERY_DATE_1') }}
            {{ $t('REPAIR_FILE_DELIVERY_CLIENT_DRAWER.DETAIL.DELIVERY_DATE_2') }}
          </p>
        </v-col>
      </v-row>
      <v-footer class="rm-drawer-footer justify-end">
        <v-btn
          :disabled="
            !deliveryClientFormValid ||
            loading ||
            !currentRepairFile.product_boomerang_id ||
            checkGuaranteeValidity()
          "
          :loading="loading"
          prepend-icon="mdi-check"
          type="submit"
        >
          {{ $t('REPAIR_FILE_DELIVERY_CLIENT_DRAWER.BUTTON.SAVE') }}
        </v-btn>
      </v-footer>
    </v-form>
  </v-container>
</template>

<script>
import RepairFileEndStatusConstant from '@/constants/repair-module/repair-file/repairFileEndStatusConstant'
import ProductService from '@/services/ProductService'
import SerialNumberService from '@/services/SerialNumberService'
import RepairFileService from '@/services/RepairFileService'
import ResponseConstant from '@/constants/responseConstant'
import RepairFileGuaranteeConstant from '@/constants/repair-module/repair-file/repairFileGuaranteeConstant'

import RepairFilePricingTypeConstant from '@/constants/repair-module/repair-file/repairFilePricingTypeConstant'
import Validators from '@/utils/validators'
import { displayToast, handleResponseError } from '@/utils/toastUtils'
import CustomDateInput from '@/components/ui/CustomDateInput.vue'
import RightDrawerHeader from '@/components/ui/RightDrawerHeader.vue'
import RepairFileSerialNumberImageModal from '@/components/repair-files/modals/RepairFileSerialNumberImageModal.vue'
import { displayModal } from '@/utils/modalUtils'

export default {
  components: { CustomDateInput, RightDrawerHeader },
  props: {
    currentRepairFile: Object,
    actionType: String || undefined,
    submitHandler: { type: Function, default: null }
  },
  data() {
    const date = new Date()
    date.setDate(date.getDate())
    date.setHours(0, 0, 0, 0)

    return {
      error: null,
      productBoomerangId: null,
      productBoomerangName: null,
      product: null,
      serialNumber: null,
      isGenericSerialNumber: false,
      productEndCost: null,
      serialNumberFormat: null,
      billingNumber: null,
      packageClientPrice: null,
      transportCost: null,
      additionalRepairCost: null,

      deliveryClientFormValid: false,
      loading: false,
      getSerialNumber: false,
      deliveryClient: false,
      showReplacementProduct: false,
      serialImageModalPending: false,

      selectedEndStatus: null,
      endStatusList: Object.keys(RepairFileEndStatusConstant).map((key) => ({
        technical_id: key,
        name: this.$t(
          `REPAIR_FILE_DELIVERY_CLIENT_DRAWER.PRODUCT_END_STATUS.${RepairFileEndStatusConstant[key]}`
        )
      })),

      selectedProduct: null,
      productList: null,

      realDeliveryDate: date,
      today: new Date(),

      requiredRules: Validators.requiredRules,
      isNumberRules: Validators.isNumberRules,
      positiveNumberRules: Validators.positiveNumberRules
    }
  },
  computed: {
    componentTitle() {
      return this.actionType === 'close'
        ? this.$t('REPAIR_FILE_DELIVERY_CLIENT_DRAWER.CLOSE_TITLE')
        : this.$t('REPAIR_FILE_DELIVERY_CLIENT_DRAWER.UPDATE_TITLE')
    }
  },
  watch: {
    async selectedEndStatus() {
      if (!this.selectedEndStatus) {
        this.deliveryClient = false
        return
      }
      this.deliveryClient = true

      if (
        this.selectedEndStatus === RepairFileEndStatusConstant.PRODUCT_REPAIRED &&
        !this.estimateOptionPreselected &&
        this.currentRepairFile.initial_pricing_type === RepairFilePricingTypeConstant.PACKAGE &&
        this.currentRepairFile.package_repairer_cost_code !== null &&
        this.currentRepairFile.package_repairer_cost_code !== 'DEVIS'
      ) {
        this.productEndCost = this.currentRepairFile.package_repairer_cost_value
      } else {
        this.productEndCost = this.getProductEndCost()
      }

      if (
        this.selectedEndStatus === RepairFileEndStatusConstant.PRODUCT_REPAIRED ||
        this.selectedEndStatus === RepairFileEndStatusConstant.PRODUCT_CREDIT ||
        this.selectedEndStatus === RepairFileEndStatusConstant.PRODUCT_RECYCLED ||
        this.selectedEndStatus === RepairFileEndStatusConstant.PRODUCT_RETURN_DISASSEMBLED
      ) {
        await this.handleProductRepairedOrRecycled()
      }
      if (
        this.selectedEndStatus === RepairFileEndStatusConstant.PRODUCT_REPLACED_STANDARD ||
        this.selectedEndStatus === RepairFileEndStatusConstant.PRODUCT_REPLACED_SUPERIOR
      ) {
        this.handleProductReplaced()
      }

      await this.getConfirmCostData()
    },
    serialNumber() {
      if (this.serialNumber) {
        this.checkFormatValidity()
      }
    },
    async selectedProduct() {
      if (this.selectedProduct) {
        const product = this.productList.find(
          (product) => product.technical_id === this.selectedProduct
        )
        await this.getProduct(product.boomerang_id)
      }
    }
  },
  async created() {
    if (!this.currentRepairFile.product_boomerang_id) {
      displayToast(
        this.$t('REPAIR_FILE_DELIVERY_CLIENT_DRAWER.MESSAGE.PRODUCT_BOOMERANG_ID_NOT_FOUND'),
        'warning'
      )
    } else {
      await this.getProductListBySubCategory()

      if (this.currentRepairFile.initial_pricing_type === RepairFilePricingTypeConstant.ESTIMATE) {
        await this.getConfirmCostData()
      }
    }
  },
  methods: {
    async openSerialNumberImage() {
      if (!this.product?.serial_number_picture || this.serialImageModalPending) return

      this.serialImageModalPending = true
      try {
        await displayModal({
          component: RepairFileSerialNumberImageModal,
          maxWidth: 720,
          props: {
            alt: this.$t('REPAIR_FILE_DELIVERY_CLIENT_DRAWER.IMAGE.SERIAL_NUMBER_ALT'),
            src: this.product.serial_number_picture,
            title: this.$t('REPAIR_FILE_DELIVERY_CLIENT_DRAWER.BUTTON.SERIAL_NUMBER')
          }
        })
      } finally {
        this.serialImageModalPending = false
      }
    },
    async getProductListBySubCategory() {
      try {
        const response = await ProductService.getProductListBySubCategory(
          this.currentRepairFile.sub_category_boomerang_id
        )
        this.productList = response.item_list
        this.patchData()
      } catch (error) {
        handleResponseError(error)
        if (error.response?.data && error.response.data.key in ResponseConstant) {
          this.setError(error.response.data.key)
        }
      }
    },
    async getConfirmCostData() {
      try {
        const response = await RepairFileService.getConfirmCost(this.currentRepairFile.technical_id)

        if (response.takeover_product_status) {
          this.selectedEndStatus = response.takeover_product_status
        }

        if (response.takeover_product_replacement_name) {
          this.productBoomerangName = response.takeover_product_replacement_name
        }

        if (response.takeover_product_replacement_boomerang_id) {
          const replacementProduct = this.productList.find(
            (product) => product.boomerang_id === response.takeover_product_replacement_boomerang_id
          )
          if (replacementProduct) this.selectedProduct = replacementProduct.technical_id
        }

        if (response.takeover_product_serial_number) {
          this.serialNumber = response.takeover_product_serial_number
        }

        if (response.takeover_billing_number) {
          this.billingNumber = response.takeover_billing_number
        }

        if (response.takeover_repairer_total_cost != null) {
          this.productEndCost = String(response.takeover_repairer_total_cost)
        }

        if (response.takeover_client_price != null) {
          this.packageClientPrice = String(response.takeover_client_price)
        }

        if (response.takeover_transport_cost != null) {
          this.transportCost = String(response.takeover_transport_cost)
        }

        if (response.takeover_additional_cost != null) {
          this.additionalRepairCost = String(response.takeover_additional_cost)
        }

        const deliveredAt =
          response.takeover_by_client_date ||
          this.currentRepairFile.client_return_transport?.delivered_at
        if (deliveredAt) {
          const [year, month, day] = deliveredAt.slice(0, 10).split('-').map(Number)
          this.realDeliveryDate = new Date(year, month - 1, day)
        }
      } catch (error) {
        handleResponseError(error)
      }
    },
    async submit() {
      try {
        const year = this.realDeliveryDate.getFullYear()
        const month = (this.realDeliveryDate.getMonth() + 1).toString().padStart(2, '0')
        const day = this.realDeliveryDate.getDate().toString().padStart(2, '0')
        const realDeliveryDateFormated = `${year}-${month}-${day}`

        const data = {
          repair_file_boomerang_id: this.currentRepairFile.boomerang_id,
          product_end_status: this.selectedEndStatus,
          product_boomerang_id: this.getProductBoomerangId(),
          product_serial_number: this.serialNumber,
          product_end_cost: this.productEndCost,
          billing_number: this.billingNumber,
          package_client_price: this.packageClientPrice,
          transport_cost: this.transportCost,
          additional_repair_cost: this.additionalRepairCost,
          real_delivery_date: realDeliveryDateFormated,
          action_type: this.actionType
        }
        if (this.submitHandler) await this.submitHandler(data)
        else await RepairFileService.updateConfirmCost(data)
        this.$emit('updateCurrentRepairFile')
        this.$emit('closeDeliveryClient')
      } catch (error) {
        handleResponseError(error)
        if (error.response?.data && error.response.data.key in ResponseConstant) {
          this.setError(error.response.data.key)
        }
      }
    },
    async handleProductRepairedOrRecycled() {
      if (
        this.selectedEndStatus === RepairFileEndStatusConstant.PRODUCT_RETURN_DISASSEMBLED &&
        this.currentRepairFile.status_guarantee === RepairFileGuaranteeConstant.NOT_GUARANTEE
      ) {
        this.productEndCost = null
        this.packageClientPrice = null
        this.transportCost = null
        this.additionalRepairCost = null
      } else {
        this.packageClientPrice = this.getPackageClientPrice()
        this.transportCost = this.getTransportCost()
        this.additionalRepairCost = this.getAdditionalRepairCost()
      }
      this.productBoomerangId = this.currentRepairFile.product_boomerang_id
      this.productBoomerangName = this.currentRepairFile.product_name
      this.serialNumber = this.currentRepairFile.product_serial_number
      if (this.currentRepairFile.product_boomerang_id) {
        await this.getProduct(this.currentRepairFile.product_boomerang_id)
      }
      await this.getModelSerialNumber()
      this.checkFormatValidity()
      this.showReplacementProduct = false
    },
    handleProductReplaced() {
      if (
        this.estimateOptionPreselected &&
        this.selectedEndStatus !== this.estimateOptionPreselected.offer_type.toUpperCase()
      ) {
        this.productEndCost = null
        this.packageClientPrice = null
        this.transportCost = null
        this.additionalRepairCost = null
      } else {
        this.packageClientPrice = this.getPackageClientPrice()
        this.transportCost = this.getTransportCost()
        this.additionalRepairCost = this.getAdditionalRepairCost()
      }
      this.serialNumber = ''
      this.showReplacementProduct = true
    },

    async getModelSerialNumber() {
      if (this.product && !this.product.model_serial_number?.format) {
        this.modelSerialNumber = await SerialNumberService.getProductTypeBySubCategory(
          this.currentRepairFile.sub_category_boomerang_id
        )
        if (this.modelSerialNumber && this.modelSerialNumber.format) {
          this.getSerialNumber = true
          this.serialNumberFormat = this.modelSerialNumber.format
        } else {
          this.serialNumberFormat = null
        }
      }
      if (this.product && this.product.model_serial_number?.format) {
        this.getSerialNumber = true
        this.serialNumberFormat = this.product.model_serial_number.format
      }
    },
    getProductBoomerangId() {
      if (
        this.selectedEndStatus === RepairFileEndStatusConstant.PRODUCT_REPAIRED ||
        this.selectedEndStatus === RepairFileEndStatusConstant.PRODUCT_CREDIT ||
        this.selectedEndStatus === RepairFileEndStatusConstant.PRODUCT_RECYCLED ||
        this.selectedEndStatus === RepairFileEndStatusConstant.PRODUCT_RETURN_DISASSEMBLED
      ) {
        return this.currentRepairFile.product_boomerang_id
      } else if (
        this.selectedEndStatus === RepairFileEndStatusConstant.PRODUCT_REPLACED_STANDARD ||
        this.selectedEndStatus === RepairFileEndStatusConstant.PRODUCT_REPLACED_SUPERIOR
      ) {
        const selectedProduct = this.productList.find(
          (product) => product.technical_id === this.selectedProduct
        )
        return selectedProduct.boomerang_id
      }
    },
    checkFormatValidity() {
      if (
        this.serialNumberFormat &&
        this.serialNumber?.length !== Number(this.serialNumberFormat)
      ) {
        this.error = {
          serialNumber: this.$t('FILE_STEP_GUARANTEE.ERROR.SERIAL_NUMBER_NOT_GOOD_FORMAT', {
            value: this.serialNumberFormat
          })
        }
        this.isGenericSerialNumber = false
      } else {
        this.error = null
        this.isGenericSerialNumber = this.checkGenericSerialNumber()
      }
    },
    checkGenericSerialNumber() {
      const regex = /^9{5,}$/
      return regex.test(this.serialNumber)
    },
    formatNumber(value) {
      if (value === null || value === 0) {
        return null
      }
      const parsedValue = parseFloat(value)
      return parsedValue % 1 === 0 ? parsedValue.toFixed(0) : parsedValue.toFixed(2)
    },
    patchData() {
      this.estimateOptionPreselected =
        this.currentRepairFile?.repairer_estimate?.repairer_estimate_option_list.find(
          (option) => option.is_selected === true
        )
      if (this.estimateOptionPreselected) {
        this.selectedEndStatus = this.estimateOptionPreselected.offer_type.toUpperCase()
        this.productEndCost = this.getProductEndCost()
      } else if (
        !this.estimateOptionPreselected &&
        this.currentRepairFile.initial_pricing_type === RepairFilePricingTypeConstant.PACKAGE
      ) {
        this.selectedEndStatus = RepairFileEndStatusConstant.PRODUCT_REPAIRED
        if (
          this.currentRepairFile.package_repairer_cost_code !== null &&
          this.currentRepairFile.package_repairer_cost_code !== 'DEVIS'
        ) {
          this.productEndCost = this.currentRepairFile.package_repairer_cost_value
        }
      } else {
        this.selectedEndStatus = null
      }

      this.billingNumber = this.currentRepairFile.takeover_billing_number || ''
      this.packageClientPrice = this.getPackageClientPrice()
      this.transportCost = this.getTransportCost()
      this.additionalRepairCost = this.getAdditionalRepairCost()
    },
    async getProduct(productBoomerangId) {
      try {
        this.product = await ProductService.getProduct(productBoomerangId)
        if (this.product?.model_serial_number?.format) {
          this.getSerialNumber = true
          this.serialNumberFormat = this.product?.model_serial_number?.format
        } else {
          this.serialNumberFormat = null
        }
        this.checkFormatValidity()
      } catch (error) {
        handleResponseError(error)
        if (error.response?.data && error.response.data.key in ResponseConstant) {
          this.setError(error.response.data.key)
        }
      }
    },
    setError(key) {
      switch (key) {
        case 'product_does_not_exist_error':
          this.error = { productBoomerangName: this.$t('ERROR.' + ResponseConstant[key]) }
          break
        default:
          this.error = ''
      }
    },
    cancelDeliveryClient() {
      this.$emit('cancelDeliveryClient')
    },
    getPackageClientPrice() {
      if (this.estimateOptionPreselected) {
        return this.estimateOptionPreselected.total_cost
      }
      if (this.currentRepairFile.status_guarantee !== RepairFileGuaranteeConstant.NOT_GUARANTEE) {
        return 0
      }
      return this.formatNumber(this.currentRepairFile.package_client_price)
    },
    getTransportCost() {
      if (this.estimateOptionPreselected) {
        return
      }
      if (this.currentRepairFile.status_guarantee !== RepairFileGuaranteeConstant.NOT_GUARANTEE) {
        return 0
      }
      return this.formatNumber(this.currentRepairFile.initial_package_transport_cost)
    },
    getAdditionalRepairCost() {
      if (this.estimateOptionPreselected) {
        return
      }
      if (this.currentRepairFile.status_guarantee !== RepairFileGuaranteeConstant.NOT_GUARANTEE) {
        return 0
      }
      return this.formatNumber(this.currentRepairFile.initial_package_additional_repair_cost)
    },
    getProductEndCost() {
      if (this.estimateOptionPreselected) {
        return (
          this.estimateOptionPreselected.estimate_cost +
          this.estimateOptionPreselected.transport_cost
        ).toFixed(2)
      }
      if (this.currentRepairFile.status_guarantee !== RepairFileGuaranteeConstant.NOT_GUARANTEE) {
        return 0
      }
      return null
    },
    getTotalCost() {
      return (
        Number(this.packageClientPrice || 0) +
        Number(this.transportCost || 0) +
        Number(this.additionalRepairCost || 0)
      ).toFixed(2)
    },
    checkGuaranteeValidity() {
      if (
        ((this.selectedEndStatus === RepairFileEndStatusConstant.PRODUCT_RECYCLED ||
          this.selectedEndStatus === RepairFileEndStatusConstant.PRODUCT_CREDIT) &&
          this.currentRepairFile.status_guarantee === RepairFileGuaranteeConstant.NOT_GUARANTEE) ||
        (this.selectedEndStatus === RepairFileEndStatusConstant.PRODUCT_RETURN_DISASSEMBLED &&
          this.currentRepairFile.status_guarantee === RepairFileGuaranteeConstant.NOT_GUARANTEE)
      ) {
        return !this.productEndCost
      }
      return false
    }
  }
}
</script>

<style scoped>
.delivery-price-card {
  border-radius: var(--default-radius);
}

.delivery-price-card {
  background-color: var(--soft-surface-color);
  border: 1px solid var(--vuetify-grey);
}

.delivery-price-row {
  align-items: center;
  display: flex;
  gap: 16px;
  justify-content: space-between;
  margin-bottom: 14px;
}

.delivery-price-label {
  color: rgba(var(--v-theme-on-surface), 0.86);
  font-size: 14px;
  font-weight: 600;
  line-height: 1.35;
}

.delivery-price-divider {
  margin-inline: -16px;
  width: calc(100% + 32px);
}

.delivery-price-total-row {
  align-items: center;
  display: flex;
  justify-content: space-between;
}

.delivery-price-total-row span,
.delivery-price-total-row strong {
  color: rgba(var(--v-theme-on-surface), 0.9);
  font-size: 16px;
  font-weight: 700;
}

.delivery-price-total-row strong {
  color: rgb(var(--v-theme-primary));
}

.number-text-field :deep(input) {
  font-weight: 400;
  padding-right: 8px;
}

.number-text-field {
  flex: 0 0 180px;
  width: 180px;
}

.euro-suffix {
  color: rgba(var(--v-theme-on-surface), 0.7);
  font-weight: 400;
  padding-left: 4px;
}

.serial-picture-btn {
  font-size: 14px;
  padding-inline: 8px;
}
</style>
